import { useMemo } from 'react';
import type { ReactNode } from 'react';

import type { WorkflowDefinition } from '../shared/index.js';
import { TICKET_PRIORITY_LABELS, visibleStages } from '../shared/index.js';

import type { BoardCard, DirectoryUser } from '../api/endpoints.js';
import { TONE_DOT, personLabel } from './ticket-card.js';

interface TicketListProps {
  readonly workflow: WorkflowDefinition;
  readonly tickets: readonly BoardCard[];
  readonly people: ReadonlyMap<string, DirectoryUser>;
  readonly onOpen: (ticketId: string) => void;
  readonly renderAction?: (ticket: BoardCard) => ReactNode;
}

const PRIORITY_TEXT: Readonly<Record<string, string>> = Object.freeze({
  high: 'text-critical',
  normal: 'text-muted',
  low: 'text-muted/70',
});

function formatDate(value: string): string {
  return new Date(value).toLocaleDateString(undefined, { day: '2-digit', month: 'short' });
}

export function TicketList({
  workflow,
  tickets,
  people,
  onOpen,
  renderAction,
}: TicketListProps): ReactNode {
  const stages = useMemo(() => visibleStages(workflow), [workflow]);

  const grouped = useMemo(() => {
    const map = new Map<string, BoardCard[]>();

    for (const stage of stages) map.set(stage.id, []);

    for (const ticket of tickets) {
      const list = map.get(ticket.stageId);
      if (list !== undefined) list.push(ticket);
    }

    for (const list of map.values()) {
      list.sort(
        (left, right) =>
          Number.parseFloat(left.boardPosition) - Number.parseFloat(right.boardPosition),
      );
    }

    return map;
  }, [stages, tickets]);

  return (
    <div className="flex-1 overflow-y-auto px-4 py-4 sm:px-7 sm:py-5">
      {stages.map((stage) => {
        const rows = grouped.get(stage.id) ?? [];

        if (rows.length === 0) return null;

        return (
          <section key={stage.id} className="mb-7">
            <header className="mb-2 flex items-center gap-2">
              <span className={`size-2 rounded-full ${TONE_DOT[stage.tone]}`} />
              <h3 className="text-[13px] font-bold text-navy dark:text-dark-ink">{stage.label}</h3>
              <span className="text-[12px] font-semibold text-muted">{rows.length}</span>
            </header>

            <div className="overflow-hidden rounded-card border border-hairline bg-white dark:border-dark-line dark:bg-dark-panel">
              {rows.map((ticket, index) => (
                <div
                  key={ticket.id}
                  className={`flex w-full items-center ${
                    index === 0 ? '' : 'border-t border-hairline dark:border-dark-line'
                  }`}
                >
                  <button
                    type="button"
                    onClick={() => {
                      onOpen(ticket.id);
                    }}
                    className="flex min-w-0 flex-1 items-center gap-4 px-4 py-3 text-left transition hover:bg-mist/70 dark:hover:bg-white/5"
                  >
                    <span className="w-16 shrink-0 text-[11.5px] font-bold tracking-[0.06em] text-muted">
                      SBG#{ticket.number}
                    </span>
                    <span className="min-w-0 flex-1 truncate text-[14px] font-semibold text-navy dark:text-dark-ink">
                      {ticket.title}
                    </span>
                    <span className="hidden w-40 shrink-0 truncate text-[12.5px] text-muted md:block">
                      {personLabel(people, ticket.assigneeId)}
                    </span>
                    <span
                      className={`hidden w-16 shrink-0 text-[12.5px] font-semibold lg:block ${
                        PRIORITY_TEXT[ticket.priority] ?? 'text-muted'
                      }`}
                    >
                      {TICKET_PRIORITY_LABELS[ticket.priority]}
                    </span>
                    <span className="w-16 shrink-0 text-right text-[12px] text-muted">
                      {formatDate(ticket.createdAt)}
                    </span>
                  </button>

                  {renderAction === undefined ? null : (
                    <span className="shrink-0 pr-3 pl-1">{renderAction(ticket)}</span>
                  )}
                </div>
              ))}
            </div>
          </section>
        );
      })}

      {tickets.length === 0 ? (
        <p className="py-16 text-center text-[14px] text-muted">
          Nothing matches these filters yet.
        </p>
      ) : null}
    </div>
  );
}
